<template>
  <h2 class="heading">Text me with news and offers</h2>

  <p class="text">
    Add your phone number to your Members Only account and be the first to know
    about exclusive news and offers
  </p>

  <BenefitIcons
    :items="{
      notification: 'Be the first to know',
    }"
  />

  <form>
    <div class="telephone-wrapper">
      <label class="field">
        <div class="input">
          <select
            class="input__element"
            :value="usersCountryCode"
            @change="handleCodeChange"
          >
            <option
              v-for="code in telephoneCodesOrdered"
              :key="code.code"
              :value="code.code"
            >
              {{ code.dial_code }}
            </option>
          </select>
          <div v-if="v$.selectedDialCode.$dirty" class="input__response">
            <ul>
              <li
                v-for="error of v$.selectedDialCode.$errors"
                :key="error.$uid"
              >
                {{ error.$message }}
              </li>
            </ul>
          </div>
        </div>
      </label>

      <label class="field">
        <div class="input">
          <input
            v-model.trim="telephone"
            type="tel"
            class="input__element"
            autocomplete="tel"
            placeholder="Phone number"
          />
          <div v-if="v$.telephone.$dirty" class="input__response">
            <ul>
              <li v-for="error of v$.telephone.$errors" :key="error.$uid">
                {{ error.$message }}
              </li>
              <li v-for="error in errors" :key="error.message">
                {{ error.message }}
              </li>
            </ul>
          </div>
        </div>
      </label>
    </div>

    <div class="legal">
      By signing up via text, you agree to receive recurring automated marketing
      messages, including cart reminders, at the phone number provided. Consent
      is not a condition of purchase. Reply STOP to unsubscribe. Reply HELP for
      help.
    </div>

    <Button full-width @click="handleFormSubmit">Save Phone Number</Button>
  </form>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { numeric, required } from "@vuelidate/validators";
import cloneDeep from "lodash-es/cloneDeep";
import Button from "@/components/Button.vue";
import telephoneCodes from "@/assets/json/telephone-codes.json";
import { mapState } from "vuex";
import BenefitIcons from "@/components/BenefitIcons.vue";
import { smsCaptureEvent } from "@/helpers/events.js";

export default {
  name: "PhoneNumberForm",
  components: {
    Button,
    BenefitIcons,
  },
  emits: ["success"],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      telephone: null,
      success: false,
      submitted: false,
      codes: telephoneCodes,
      selectedDialCode: null,
      usersCountryCode: null,
      errors: [],
    };
  },
  computed: {
    ...mapState("auth", ["customer"]),
    fullTelephoneNumber() {
      return this.selectedDialCode + this.telephone?.toString();
    },
    telephoneCodesOrdered() {
      const countryCodesToMoveToTop = ["GB", "US", "AU"];
      const codes = cloneDeep(telephoneCodes);

      return codes
        .sort((a, b) => a["dial_code"] - b["dial_code"])
        .sort((a, b) => {
          return countryCodesToMoveToTop.includes(a.code)
            ? -1
            : countryCodesToMoveToTop.includes(b.code)
            ? 1
            : 0;
        });
    },
  },
  mounted() {
    this.preSelectUsersTelephoneCode();
  },
  methods: {
    handleCodeChange(event) {
      const value = event.target.value;
      this.usersCountryCode = value;

      const { dial_code } = this.codes.find((code) => {
        return code.code === value;
      });

      this.selectedDialCode = dial_code;
    },
    async preSelectUsersTelephoneCode() {
      const response = await fetch(
        "https://pro.ip-api.com/json?key=Y6SLxPFTt7gdeOe"
      );
      const { countryCode: usersCountryCode } = await response.json();

      const { dial_code } = this.codes.find(
        (code) => code.code === usersCountryCode
      );

      this.selectedDialCode = dial_code;
      this.usersCountryCode = usersCountryCode;
    },
    async handleFormSubmit() {
      this.v$.$touch();

      if (this.v$.$invalid) {
        return;
      }

      const customerId = this.customer.id;

      const input = {
        id: customerId,
        phone: this.fullTelephoneNumber,
        smsConsented: true,
      };

      const resp = await fetch(
        `${process.env.RATANDBOA_NODE_APP_URL}/customer/account`,
        {
          method: "post",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(input),
        }
      );

      this.submitted = true;

      if (!resp.ok) {
        const json = await resp.json();
        this.errors = json.errors;
        this.success = false;
        return;
      }

      this.success = true;

      smsCaptureEvent({ location: "data collection" });

      this.$emit("success");
    },
  },
  validations() {
    return {
      telephone: { required, numeric },
      selectedDialCode: { required },
    };
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/components/form.scss";
.legal {
  font-size: 11px;
  margin: 0 0 30px 0;
}

.heading {
  margin-top: 20px;
  text-align: center;
  font-size: 28px;
  font-weight: 400;
  text-transform: uppercase;
}

.text {
  font-size: 13px;
  margin: 10px 0 50px 0;
  text-align: center;
  font-weight: 400;
  font-family: $body-font-family;
}

::v-deep(.loyalty-benefits-icons) {
  margin: 30px auto;

  @include respond-above(md) {
    margin: 30px auto;
  }

  p {
    font-size: 13px;
  }
}
</style>
